/* =============================================================================
 * RedeemContext — promo/access-code capture + auto-redeem-on-auth
 * -----------------------------------------------------------------------------
 * Powers the public /redeem surface (Phase 0.5 — "couldn't find where to
 * redeem"). Mirrors ReferralContext's proven pattern exactly:
 *   - capture a code to localStorage (30-day expiry) while logged out
 *   - auto-redeem it the moment the user becomes authenticated
 * so a redeem link works whether or not the recipient has an account yet.
 *
 * IMPORTANT: this is DISTINCT from ReferralContext. Referral codes are partner
 * attribution (POST /api/user/referral-code). These are RevenueCat promo/access
 * codes that grant an entitlement (revenueCatAPI.validate/redeemPromoCode).
 * Different storage keys (aps_redeem_*), different backend, never cross-wired.
 *
 * Promo codes always grant the 'regular' tier (owner decision, audit #213) —
 * the selected plan is irrelevant to a promo grant.
 * ========================================================================== */
import React, {
  createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { revenueCatAPI } from '../services/api';

const STORAGE_KEY = 'aps_redeem_code';
const STORAGE_TIMESTAMP_KEY = 'aps_redeem_timestamp';
const CODE_EXPIRY_DAYS = 30;
const PROMO_TIER: 'regular' = 'regular'; // audit #213 — promos always grant regular

export type RedeemStatus = 'redeemed' | 'discount' | 'needs_action' | 'invalid' | 'error';

export interface RedeemResult {
  status: RedeemStatus;
  message: string;
  code: string;
  /** Populated when status === 'redeemed'. */
  entitlement?: string;
  isLifetime?: boolean;
  expiresAt?: string | null;
}

interface RedeemContextType {
  pendingCode: string | null;
  isRedeeming: boolean;
  lastResult: RedeemResult | null;
  captureCode: (code: string) => void;
  clearPendingCode: () => void;
  /** Validate + redeem a code for the signed-in user. Requires auth. */
  redeemCode: (code: string) => Promise<RedeemResult>;
}

const RedeemContext = createContext<RedeemContextType | undefined>(undefined);

export const useRedeem = (): RedeemContextType => {
  const ctx = useContext(RedeemContext);
  if (!ctx) throw new Error('useRedeem must be used within a RedeemProvider');
  return ctx;
};

const normalize = (code: string) => code.trim().toUpperCase();

export const RedeemProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, sessionToken } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [lastResult, setLastResult] = useState<RedeemResult | null>(null);

  // Guards a single auto-redeem attempt per code per session so a transient
  // network failure (or a discount that routes to checkout) can't spin the
  // effect forever.
  const attemptedRef = useRef<Set<string>>(new Set());

  // Load a pending code from localStorage on mount (30-day expiry, like ReferralContext).
  useEffect(() => {
    const storedCode = localStorage.getItem(STORAGE_KEY);
    const storedTs = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
    if (storedCode && storedTs) {
      const age = Date.now() - parseInt(storedTs, 10);
      if (age < CODE_EXPIRY_DAYS * 24 * 60 * 60 * 1000) {
        setPendingCode(storedCode);
      } else {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      }
    }
  }, []);

  const captureCode = useCallback((code: string) => {
    const normalized = normalize(code);
    if (!normalized) return;
    localStorage.setItem(STORAGE_KEY, normalized);
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
    setPendingCode(normalized);
  }, []);

  const clearPendingCode = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
    setPendingCode(null);
  }, []);

  const redeemCode = useCallback(async (rawCode: string): Promise<RedeemResult> => {
    const code = normalize(rawCode);
    if (!sessionToken) {
      return { status: 'error', message: 'You must be signed in to redeem a code.', code };
    }

    setIsRedeeming(true);
    try {
      const validate = await revenueCatAPI.validatePromoCode(sessionToken, code, PROMO_TIER);

      if (!validate.valid) {
        const result: RedeemResult = {
          status: 'invalid',
          message: validate.message || validate.error || 'This code is invalid or has expired.',
          code,
        };
        setLastResult(result);
        return result;
      }

      // Discount codes can't be granted directly — they require Stripe Checkout.
      // Surface as a distinct status so the caller can route to the paywall,
      // where the existing promo-at-checkout flow applies the coupon.
      if (validate.type === 'discount') {
        const result: RedeemResult = {
          status: 'discount',
          message: validate.description || 'This is a discount code. Apply it at checkout to subscribe.',
          code,
        };
        setLastResult(result);
        return result;
      }

      // Free access code — grant the entitlement immediately.
      const redeem = await revenueCatAPI.redeemPromoCode(sessionToken, code, PROMO_TIER);

      // The code is valid but the user has an existing paid subscription — the
      // backend needs them to cancel/confirm first (409). Don't discard a valid
      // code as "invalid"; surface a distinct actionable status.
      if (!redeem.success && (redeem.requiresCancellationFirst || redeem.requiresConfirmation)) {
        const result: RedeemResult = {
          status: 'needs_action',
          message: redeem.confirmationMessage || redeem.message ||
            'You already have an active subscription. Manage it before redeeming this code.',
          code,
        };
        setLastResult(result);
        return result;
      }

      if (redeem.success) {
        const result: RedeemResult = {
          status: 'redeemed',
          message: redeem.message || 'Your access has been unlocked.',
          code,
          entitlement: redeem.entitlement,
          isLifetime: redeem.isLifetime,
          expiresAt: redeem.expiresAt,
        };
        setLastResult(result);
        return result;
      }

      const failed: RedeemResult = {
        status: 'invalid',
        message: redeem.message || redeem.error || 'This code could not be redeemed.',
        code,
      };
      setLastResult(failed);
      return failed;
    } catch (err: any) {
      const result: RedeemResult = {
        status: 'error',
        message: err?.message || 'Something went wrong redeeming your code. Please try again.',
        code,
      };
      setLastResult(result);
      return result;
    } finally {
      setIsRedeeming(false);
    }
  }, [sessionToken]);

  // Auto-redeem a captured code once the user is authenticated. This is the
  // magic that makes a logged-out redeem link "just work" after signup.
  useEffect(() => {
    if (!isAuthenticated || !sessionToken || !pendingCode || isRedeeming) return;
    if (attemptedRef.current.has(pendingCode)) return;
    attemptedRef.current.add(pendingCode);

    (async () => {
      const result = await redeemCode(pendingCode);
      if (result.status === 'redeemed') {
        clearPendingCode();
        toast.success(result.message || 'Your access has been unlocked!');
      } else if (result.status === 'discount') {
        // Can't auto-grant a discount; hand off to the paywall where the promo
        // input lives. Clear the pending code so we don't loop.
        clearPendingCode();
        toast.info(`Enter code ${result.code} at checkout to apply your discount.`);
        // Pass the code through so the paywall can pre-fill it rather than making
        // the user retype it.
        navigate(`/subscription-required?code=${encodeURIComponent(result.code)}`, { replace: true });
      } else if (result.status === 'needs_action') {
        // Valid code, but the user must manage an existing subscription first.
        // Clear so it doesn't retry each login; the toast tells them what to do.
        clearPendingCode();
        toast.info(result.message);
      } else if (result.status === 'invalid') {
        // A bad/expired code should not haunt every future login.
        clearPendingCode();
        toast.error(result.message);
      }
      // On 'error' (transient), keep the code; attemptedRef stops a same-session loop.
    })();
  }, [isAuthenticated, sessionToken, pendingCode, isRedeeming, redeemCode, clearPendingCode, toast, navigate]);

  const value: RedeemContextType = {
    pendingCode,
    isRedeeming,
    lastResult,
    captureCode,
    clearPendingCode,
    redeemCode,
  };

  return <RedeemContext.Provider value={value}>{children}</RedeemContext.Provider>;
};

export default RedeemContext;
